// /app/frontend/src/pages/seo/StateOfBlackPolyFamilies2026Page.jsx
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  buildArticleSchema,
  buildFaqSchema,
  buildBreadcrumbSchema,
  ReportHero,
  ReportSection,
  AboutThisReport,
  PullQuote,
  FaqList,
  BottomCTA,
} from './SEOUtils';

const URL_PATH = '/state-of-black-poly-families-2026';
const PUBLISHED_ISO = '2026-02-15';
const PUBLISHED_HUMAN = 'February 2026';

const FAQS = [
  {
    q: 'What is a poly family?',
    a: 'A poly family is a household and emotional unit built around polyamorous or plural relationships — typically including more than two adults in committed romantic partnerships, often with shared parenting, household, or financial responsibilities. The exact shape varies; what unites poly families is consent, transparency, and commitment.',
  },
  {
    q: 'How do poly families work?',
    a: 'Healthy poly families work through explicit structures — agreed-upon time rhythms, communication protocols, decision-making models, and clear boundaries. They function less like accidental arrangements and more like intentional organizations, with regular check-ins and a willingness to renegotiate as the family evolves.',
  },
  {
    q: 'Can poly families raise children successfully?',
    a: 'Yes. Children raised in healthy poly families often describe a kind of abundance — more loving adults, more attention, broader cultural exposure. Success depends on the adults coordinating clearly on parenting philosophy, language used in public, and the role of each adult in each child\u2019s life. Triangulation is the most common harm to avoid.',
  },
  {
    q: 'How do poly families manage communication?',
    a: 'The strongest poly families treat communication as infrastructure, not as a response to problems. This typically includes regular family-wide meetings, one-on-one check-ins between every pair, and a culture of naming hard feelings early rather than letting them accumulate.',
  },
  {
    q: 'What are common challenges?',
    a: 'The most common challenges are unequal time that goes unspoken, financial opacity, triangulation between partners, outside pressure from family or community, and pacing issues when new partners enter an existing structure. None of these are unsolvable when surfaced early.',
  },
];

export default function StateOfBlackPolyFamilies2026Page() {
  const schemas = [
    organizationSchema,
    buildArticleSchema({
      headline: 'The State of Black Poly Families Report 2026',
      description:
        'Exploring communication, parenting, family-building, relationship structures, and intentional community among Black poly families.',
      urlPath: URL_PATH,
      datePublishedISO: PUBLISHED_ISO,
    }),
    buildFaqSchema(FAQS),
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Reports', url: '/reports' },
      { name: 'State of Black Poly Families 2026', url: URL_PATH },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="State of Black Poly Families Report 2026 | UnityLove"
        description="Exploring communication, parenting, family-building, relationship structures, and intentional community among Black poly families."
        canonical={URL_PATH}
        schemas={schemas}
      />

      <ReportHero
        eyebrow="UnityLove Research"
        dateline={`Published ${PUBLISHED_HUMAN}`}
        headline="The State of Black Poly Families Report 2026"
        dek="An honest look at how Black poly families are forming, what they share in common, the challenges they navigate, and the future of plural family-building in the diaspora."
      />

      <article className="max-w-3xl mx-auto px-4 py-12">
        <ReportSection n={1} title="Executive Summary">
          <p>
            Black poly family-building is moving from the margins to a visible and growing dimension of Black relationship life in 2026. Some of this growth is genuinely new — couples and singles exploring ENM for the first time. Much of it is recovery — communities reclaiming relationship structures that have always existed in Black diaspora life but were pushed underground by external judgment.
          </p>
          <p>
            What we are seeing across the UnityLove community: poly family-building is being approached with significantly more intention than the casual narrative suggests. Families form slowly. Conversations precede commitment. Communication infrastructure is built deliberately. The Black poly families that thrive look more like intentional small organizations than accidental arrangements.
          </p>
        </ReportSection>

        <ReportSection n={2} title="Understanding Modern Poly Families">
          <p>
            The term &ldquo;poly family&rdquo; covers more shapes than the headline suggests. The most common structures we observe:
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1.5">
            <li><strong className="text-slate-900">Hierarchical poly</strong> — one primary couple plus additional loved partners</li>
            <li><strong className="text-slate-900">Triads / closed throuples</strong> — three committed partners, often sharing household</li>
            <li><strong className="text-slate-900">Plural families with sister wives</strong> — long-term, often faith-rooted, multi-wife families</li>
            <li><strong className="text-slate-900">Polyamory with separate partners</strong> — each partner has their own additional partners with limited overlap</li>
            <li><strong className="text-slate-900">Kitchen-table poly</strong> — all metamours comfortable sharing space and friendship</li>
          </ul>
          <p>
            None of these is more legitimate than another. The healthiest version of any of them comes from the adults inside making the choice consciously together.
          </p>
        </ReportSection>

        <ReportSection n={3} title="Common Goals">
          <p>
            Across these varied structures, certain goals show up consistently:
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1.5">
            <li><strong className="text-slate-900">Partnership</strong> — deep emotional and romantic connection with multiple adults</li>
            <li><strong className="text-slate-900">Family-building</strong> — children, household, intergenerational continuity</li>
            <li><strong className="text-slate-900">Shared responsibility</strong> — financial, domestic, emotional labor distributed across more adults</li>
            <li><strong className="text-slate-900">Emotional support</strong> — a built-in care network larger than a two-person household can provide</li>
          </ul>
        </ReportSection>

        <ReportSection n={4} title="What Healthy Poly Families Have In Common">
          <p>
            The strongest Black poly families we have spoken with share four practices, regardless of structure or faith framing:
          </p>
          <ol className="list-decimal list-inside ml-2 space-y-1.5">
            <li><strong className="text-slate-900">Communication infrastructure</strong> — scheduled, regular, not reactive</li>
            <li><strong className="text-slate-900">Transparency</strong> — information flows; no partner is hidden from another</li>
            <li><strong className="text-slate-900">Boundaries</strong> — explicit and written, not assumed</li>
            <li><strong className="text-slate-900">Shared expectations</strong> — alignment on family vision, finances, parenting, faith framing</li>
          </ol>
          <PullQuote>
            The plural families that last are not the ones with the strongest chemistry. They are the ones with the most explicit agreements.
          </PullQuote>
        </ReportSection>

        <ReportSection n={5} title="Challenges Families Face">
          <p>
            Even in healthy structures, certain challenges recur:
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1.5">
            <li>Outside judgment from family of origin, faith community, or workplace</li>
            <li>Legal asymmetry — only one marriage is legally recognized in most jurisdictions</li>
            <li>The slow drift of unequal time, especially as new partners enter</li>
            <li>Financial opacity becoming the foundation of resentment</li>
            <li>One adult becoming the emotional courier between others</li>
          </ul>
          <p>
            None of these are death sentences. All of them deserve naming early.
          </p>
        </ReportSection>

        <ReportSection n={6} title="Community And Support Systems">
          <p>
            One of the most important shifts we are seeing is the development of explicit community infrastructure for Black poly families. Online communities, in-person meetups, faith spaces that welcome plural family life, therapists trained in poly dynamics, and platforms (like UnityLove) that center poly-friendly dating.
          </p>
          <p>
            The era of isolated plural families navigating the work alone is ending. Families increasingly have peers — other families further along the path, willing to share what they have learned.
          </p>
        </ReportSection>

        <ReportSection n={7} title="Parenting Considerations">
          <p>
            Children inside healthy poly families often describe a kind of abundance — more loving adults, more attention. The risk is not the structure; it is uncoordinated adults. Strong poly families align on parenting philosophy, the language used about the family in public, and the explicit role each adult plays in each child&rsquo;s life. Disagreements are normal; triangulation is the harm to avoid.
          </p>
        </ReportSection>

        <ReportSection n={8} title="Future Trends">
          <p>
            Looking toward 2027 and beyond, three shifts seem most likely:
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1.5">
            <li><strong className="text-slate-900">Greater visibility</strong> — Black poly families telling their own stories publicly rather than letting them be told by outsiders</li>
            <li><strong className="text-slate-900">Better tooling</strong> — apps, planners, therapy resources, legal frameworks designed for plural families</li>
            <li><strong className="text-slate-900">Community continuity</strong> — peer networks of poly families that span generations, not just current connections</li>
          </ul>
        </ReportSection>

        {/* FAQ — also paired with JSON-LD schema for rich snippets */}
        <ReportSection n={9} title="Frequently Asked Questions">
          <p className="mb-2">
            Questions we hear most often from members new to poly family-building.
          </p>
          <FaqList items={FAQS} />
        </ReportSection>

        <AboutThisReport published={PUBLISHED_HUMAN} />

        {/* Internal links */}
        <section className="mt-12">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">Read Next</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/how-to-find-a-sister-wife" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">How To Find A Sister Wife</p>
              <p className="text-sm text-slate-600 mt-1">A respectful step-by-step guide.</p>
            </a>
            <a href="/black-sister-wife-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Black Sister Wife Dating</p>
              <p className="text-sm text-slate-600 mt-1">A community for plural family-building.</p>
            </a>
            <a href="/poly-family-planning" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Poly Family Planning</p>
              <p className="text-sm text-slate-600 mt-1">Practical planning checklist.</p>
            </a>
            <a href="/building-a-polygamous-family" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Building A Polygamous Family</p>
              <p className="text-sm text-slate-600 mt-1">Values and foundation.</p>
            </a>
            <a href="/safe-poly-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition sm:col-span-2">
              <p className="font-semibold text-slate-900">Safe Poly Dating Guide</p>
              <p className="text-sm text-slate-600 mt-1">Tactical safety practices.</p>
            </a>
          </div>
        </section>
      </article>

      <BottomCTA />
    </div>
  );
}
